import type { Dialog as BaseDialog } from '@base-ui/react'
import React, { type FC, useEffect, useRef, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog'
import {
	AlertCircleIcon,
	CheckCircleIcon,
	CheckIcon,
	Copy01Icon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { useGenerateQuote } from '@/features/luxe-submissions/queries/use-generate-quote'
import { cn } from '@/helpers/utils'
import useCopyToClipboard from '@/hooks/use-copy-to-clipboard'
import { type TKey, useTranslation } from '@/translations/i18n'

interface ListingWithQuote {
	submissionId: string
	quoteLink: string | null
	hostEmail?: string | null
}

interface SkippedListing {
	submissionId: string
	reason: string
}

interface GenerateQuoteDialogProps {
	handle: BaseDialog.Handle<{
		listingIds: string[]
	}>
	onSuccess?: () => void
}

export const GenerateQuoteDialog: FC<GenerateQuoteDialogProps> = ({
	handle,
	onSuccess
}) => {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)
	const [step, setStep] = useState<'confirm' | 'success'>('confirm')
	const [listingsWithQuotes, setListingsWithQuotes] = useState<
		ListingWithQuote[]
	>([])
	const [skippedListings, setSkippedListings] = useState<SkippedListing[]>([])

	const handleClose = () => {
		onSuccess?.()
		setStep('confirm')
		setListingsWithQuotes([])
		setSkippedListings([])
	}
	return (
		<Dialog
			handle={handle}
			onOpenChange={(open) => {
				setOpen(open)
				step === 'success' && !open && handleClose()
			}}
			open={open}
		>
			{({ payload }) => {
				const data = payload as { listingIds: string[] } | undefined
				return (
					data !== undefined && (
						<DialogContent
							className='grid max-w-120 grid-rows-[auto_1fr_auto] overflow-hidden'
							showCloseButton={false}
						>
							<DialogHeader>
								{t('luxe_submissions:quote_generation')}
							</DialogHeader>

							{step === 'confirm' ? (
								<ConfirmStep
									listingIds={data.listingIds}
									onSuccess={(generated, skipped) => {
										setListingsWithQuotes(generated)
										setSkippedListings(skipped)
										setStep('success')
									}}
								/>
							) : (
								<SuccessStep
									listings={listingsWithQuotes}
									skipped={skippedListings}
								/>
							)}
						</DialogContent>
					)
				)
			}}
		</Dialog>
	)
}

function ConfirmStep({
	listingIds,
	onSuccess
}: {
	listingIds: string[]
	onSuccess: (
		generated: ListingWithQuote[],
		skipped: SkippedListing[]
	) => void
}) {
	const { t } = useTranslation()
	const [blockEmail, setBlockEmail] = useState(false)
	const { mutate, isPending } = useGenerateQuote({
		onSuccess: ({ generated, skipped }) => onSuccess(generated, skipped)
	})

	return (
		<>
			<DialogBody>
				<DialogTitle>
					{t('luxe_submissions:generate_quote_description')}
				</DialogTitle>

				<div className='flex flex-col gap-2 text-base text-neutral-900'>
					<p>{t('luxe_submissions:generate_quote_listing_intro')}</p>
					<div className='flex justify-between rounded-lg bg-grey-100 p-4'>
						<p className='text-neutral-900 text-xs'>
							{t('luxe_submissions:listing', {
								count: listingIds.length
							}).toLocaleUpperCase()}
						</p>
						<p className='flex flex-col gap-1 font-semibold text-md text-neutral-900'>
							{listingIds.map((id) => (
								<span key={id}>#{id}</span>
							))}
						</p>
					</div>
				</div>

				{listingIds.length > 1 && (
					<div className='flex items-start gap-3'>
						<Switch checked={blockEmail} onChange={setBlockEmail} />
						<div className='flex flex-col'>
							<span className='font-semibold text-sm'>
								{t('luxe_submissions:block_email_generation')}
							</span>
							<span className='text-neutral-500 text-sm'>
								{t(
									'luxe_submissions:block_email_generation_description'
								)}
							</span>
						</div>
					</div>
				)}
			</DialogBody>

			<DialogFooter className='flex flex-row justify-between bg-neutral-white'>
				<DialogClose
					render={<ButtonLegacy type='button' variant='tertiary' />}
				>
					{t('common:close')}
				</DialogClose>
				<ButtonLegacy
					disabled={isPending}
					onClick={() =>
						mutate({
							params: {
								submissionIds: listingIds,
								blockEmail
							}
						})
					}
					type='button'
					variant='brand'
				>
					{t('common:confirm')}
					{isPending && <Spinner />}
				</ButtonLegacy>
			</DialogFooter>
		</>
	)
}

function SuccessStep({
	listings,
	skipped
}: {
	listings: ListingWithQuote[]
	skipped: SkippedListing[]
}) {
	const { t } = useTranslation()

	return (
		<>
			<DialogBody>
				<DialogTitle>
					{t('luxe_submissions:quote_generated_recap')}
				</DialogTitle>

				<div className='flex flex-col gap-4'>
					{listings.length > 0 && (
						<>
							<div className='flex size-9.5 items-center justify-center rounded-full bg-success-100'>
								<CheckCircleIcon
									className='text-success-600'
									size={16}
								/>
							</div>
							<DialogDescription>
								{t('luxe_submissions:quote_link_ready_for')}
							</DialogDescription>
							<ListingsSummary listings={listings} />
						</>
					)}

					{skipped.length > 0 && (
						<div
							className={cn(
								'flex flex-col gap-2',
								listings.length > 0 &&
									'mt-2 border-grey-300 border-t pt-6'
							)}
						>
							<div className='flex size-9.5 items-center justify-center rounded-full bg-danger-100'>
								<AlertCircleIcon
									className='text-danger-600'
									size={16}
								/>
							</div>
							<DialogTitle>
								{t('luxe_submissions:quote_not_generated_for')}
							</DialogTitle>
							<div className='flex flex-col gap-3'>
								{skipped.map((l) => (
									<div
										className='flex flex-col gap-2'
										key={l.submissionId}
									>
										<div className='flex justify-between gap-2 rounded-lg bg-grey-100 p-4'>
											<p className='text-neutral-900 text-xs'>
												{t('luxe_submissions:listing', {
													count: 1
												}).toLocaleUpperCase()}
											</p>
											<p className='font-semibold text-md text-neutral-900'>
												#{l.submissionId}
											</p>
										</div>
										<p className='text-primary-600 text-sm'>
											{t(
												`luxe_submissions:quote_generation_error.${l.reason}` as TKey
											)}
										</p>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</DialogBody>
			<DialogFooter className='flex flex-row justify-end'>
				<DialogClose
					render={<ButtonLegacy type='button' variant='brand' />}
				>
					{t('common:done')}
				</DialogClose>
			</DialogFooter>
		</>
	)
}

function ListingsSummary({ listings }: { listings: ListingWithQuote[] }) {
	const { t } = useTranslation()
	const [copiedLink, setCopiedLink] = useState('')
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const { copyToClipboard } = useCopyToClipboard((text) => {
		setCopiedLink(text)
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		timeoutRef.current = setTimeout(() => setCopiedLink(''), 3000)
	})

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	return (
		<div className='flex flex-col gap-3'>
			{listings.map((l) => {
				const quoteLink = l.quoteLink

				return (
					<React.Fragment key={l.submissionId}>
						<div className='flex flex-col gap-3 rounded-lg bg-grey-100 p-4'>
							<div className='flex justify-between gap-2'>
								<p className='text-neutral-900 text-xs'>
									{t('luxe_submissions:listing', {
										count: 1
									}).toLocaleUpperCase()}
								</p>
								<p className='flex flex-col gap-1 font-semibold text-md text-neutral-900'>
									<span>#{l.submissionId}</span>
								</p>
							</div>

							{l.hostEmail && (
								<div className='flex justify-between gap-2 border-neutral-300 border-t pt-2'>
									<p className='whitespace-nowrap text-neutral-900 text-xs'>
										{t(
											'luxe_submissions:host_email'
										).toLocaleUpperCase()}
									</p>
									<p className='font-semibold text-md text-neutral-900'>
										{l.hostEmail}
									</p>
								</div>
							)}
						</div>
						{quoteLink && (
							<div className='flex w-full flex-col gap-2'>
								<p className='text-neutral-900 text-sm'>
									{t(
										'luxe_submissions:copy_quote_instruction'
									)}
								</p>

								<div className='grid grid-cols-[1fr_auto] truncate rounded-lg border border-neutral-300 text-base text-neutral-500'>
									<input
										className='px-3 py-2'
										id={`quote-link-input-${l.submissionId}`}
										readOnly
										value={quoteLink}
									/>

									<button
										className={cn(
											'flex cursor-pointer items-center gap-2 border-neutral-300 border-l px-5 py-2.5 font-medium text-base text-neutral-700',
											copiedLink === quoteLink &&
												'text-success-600'
										)}
										onClick={() =>
											copyToClipboard(quoteLink)
										}
										type='button'
									>
										{copiedLink === quoteLink ? (
											<>
												<CheckIcon size={20} />
												{t('common:copied')}
											</>
										) : (
											<>
												<Copy01Icon size={20} />
												{t('common:copy')}
											</>
										)}
									</button>
								</div>
							</div>
						)}
					</React.Fragment>
				)
			})}
		</div>
	)
}
